import { useState, useCallback, useMemo } from 'react';

const MAX_DEFAULT = 3;

function compareValues(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  if (typeof a === 'number' && typeof b === 'number') return a - b;

  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();

  const aDate = typeof a === 'string' ? Date.parse(a) : NaN;
  const bDate = typeof b === 'string' ? Date.parse(b) : NaN;
  if (!Number.isNaN(aDate) && !Number.isNaN(bDate) && /^\d{4}-\d{2}-\d{2}/.test(String(a))) {
    return aDate - bDate;
  }

  const aNum = typeof a === 'string' ? Number(a) : NaN;
  const bNum = typeof b === 'string' ? Number(b) : NaN;
  if (!Number.isNaN(aNum) && !Number.isNaN(bNum) && a !== '' && b !== '') {
    return aNum - bNum;
  }

  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
}

/**
 * Hook for managing multi-column 3-state table sort state.
 *
 * State is an array of { column, dir } where dir is 'asc' | 'desc'. Empty array = default ordering.
 *
 * Click semantics:
 * - Plain click on a new column → replace with [{column, 'asc'}]
 * - Plain click on the active primary column → cycle asc → desc → cleared
 * - Shift-click on a new column → append { column, 'asc' } (capped at `max`)
 * - Shift-click on a column already in the list → cycle that column's dir; remove if it was desc
 *
 * @param {object} [options]
 * @param {Array<{column:string,dir:'asc'|'desc'}>} [options.defaultSort=[]]
 * @param {Function} [options.onSortChange] - fires whenever sorts change (e.g. wire to pagination.resetPage)
 * @param {number} [options.max=3] - max simultaneous sort columns
 */
const useTableSort = (options = {}) => {
  const { defaultSort = [], onSortChange, max = MAX_DEFAULT } = options;

  const [sorts, setSorts] = useState(defaultSort);

  const updateSorts = useCallback((updater) => {
    setSorts((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (onSortChange) onSortChange();
      return next;
    });
  }, [onSortChange]);

  const handleSort = useCallback((columnKey, event) => {
    const additive = !!(event && event.shiftKey);
    updateSorts((prev) => {
      const idx = prev.findIndex((s) => s.column === columnKey);

      if (!additive) {
        if (idx === 0 && prev.length === 1) {
          if (prev[0].dir === 'asc') return [{ column: columnKey, dir: 'desc' }];
          return [];
        }
        return [{ column: columnKey, dir: 'asc' }];
      }

      if (idx === -1) {
        if (prev.length >= max) return prev;
        return [...prev, { column: columnKey, dir: 'asc' }];
      }

      const current = prev[idx];
      if (current.dir === 'asc') {
        const copy = prev.slice();
        copy[idx] = { column: columnKey, dir: 'desc' };
        return copy;
      }
      return prev.filter((_, i) => i !== idx);
    });
  }, [updateSorts, max]);

  const reset = useCallback(() => {
    updateSorts([]);
  }, [updateSorts]);

  const sortRows = useCallback((rows, columns) => {
    if (!Array.isArray(rows) || sorts.length === 0) return rows;

    const colMap = new Map();
    if (Array.isArray(columns)) {
      columns.forEach((c) => colMap.set(c.key, c));
    }

    const indexed = rows.map((row, i) => ({ row, i }));
    indexed.sort((a, b) => {
      for (const { column, dir } of sorts) {
        const col = colMap.get(column);
        const getValue = col?.getValue || ((r) => r[column]);
        const av = getValue(a.row);
        const bv = getValue(b.row);
        const cmp = compareValues(av, bv);
        if (cmp !== 0) return dir === 'desc' ? -cmp : cmp;
      }
      const aId = a.row?.id;
      const bId = b.row?.id;
      if (aId != null && bId != null && aId !== bId) return aId < bId ? -1 : 1;
      return a.i - b.i;
    });
    return indexed.map((x) => x.row);
  }, [sorts]);

  const apiParams = useMemo(() => {
    if (sorts.length === 0) return {};
    return {
      sort_by: sorts.map((s) => s.column).join(','),
      sort_dir: sorts.map((s) => s.dir).join(','),
    };
  }, [sorts]);

  return useMemo(() => ({
    sorts,
    handleSort,
    sortRows,
    reset,
    apiParams,
  }), [sorts, handleSort, sortRows, reset, apiParams]);
};

export default useTableSort;
